/**
 * Port of the Mac app's `PortfolioCalculator` — average-cost-basis position maths.
 *
 * The one rule that governs every function here: a percentage is only ever computed against
 * the baseline it actually belongs to. The Mac app had a real bug where a day-change percentage
 * and a since-purchase percentage got crossed, producing numbers that looked plausible and were
 * completely wrong. Each function below therefore names its baseline explicitly rather than
 * returning a bare "percent".
 */

import type { DemoPosition, DemoStock } from './types';

export interface PricedPosition {
  ticker: string;
  name: string;
  shares: number;
  averageCost: number;
  price: number;
  previousClose: number;

  /** shares × price — what it's worth now. */
  marketValue: number;
  /** shares × averageCost — what was actually paid. */
  invested: number;
  /** marketValue − invested. */
  unrealizedPnL: number;
  /** Against the COST BASIS: (price − averageCost) / averageCost. */
  returnSincePurchase: number;
  /** Against YESTERDAY'S CLOSE: shares × (price − previousClose). A money amount, not a ratio. */
  dayPnL: number;
  /** Against yesterday's close, as a ratio: (price − previousClose) / previousClose. */
  dayChange: number;
  /** This position's share of total portfolio market value. Filled in by `buildPortfolio`. */
  weight: number;
}

export interface PortfolioTotals {
  marketValue: number;
  invested: number;
  unrealizedPnL: number;
  /** Against total INVESTED — not against yesterday, and not an average of the rows. */
  totalReturn: number;
  dayPnL: number;
  /** Day P&L against yesterday's total value, i.e. (value − yesterdayValue) / yesterdayValue. */
  dayChange: number;
  positions: PricedPosition[];
}

export function buildPortfolio(
  positions: DemoPosition[],
  stocksByTicker: Map<string, DemoStock>,
): PortfolioTotals {
  const priced: PricedPosition[] = [];

  for (const p of positions) {
    const s = stocksByTicker.get(p.ticker);
    if (!s) continue; // A position with no quote is skipped rather than valued at zero.

    const marketValue = p.shares * s.price;
    const invested = p.shares * p.averageCost;

    priced.push({
      ticker: p.ticker,
      name: s.name,
      shares: p.shares,
      averageCost: p.averageCost,
      price: s.price,
      previousClose: s.previousClose,
      marketValue,
      invested,
      unrealizedPnL: marketValue - invested,
      returnSincePurchase: (s.price - p.averageCost) / p.averageCost,
      dayPnL: p.shares * (s.price - s.previousClose),
      dayChange: (s.price - s.previousClose) / s.previousClose,
      weight: 0,
    });
  }

  const marketValue = priced.reduce((sum, p) => sum + p.marketValue, 0);
  const invested = priced.reduce((sum, p) => sum + p.invested, 0);
  const dayPnL = priced.reduce((sum, p) => sum + p.dayPnL, 0);

  for (const p of priced) {
    p.weight = marketValue > 0 ? p.marketValue / marketValue : 0;
  }

  // Yesterday's total, reconstructed from the same rows — so the day percentage is the
  // portfolio's own movement and NOT a weighted average of per-row percentages, which is a
  // different (and wrong) number whenever position sizes differ.
  const yesterdayValue = priced.reduce((sum, p) => sum + p.shares * p.previousClose, 0);

  return {
    marketValue,
    invested,
    unrealizedPnL: marketValue - invested,
    totalReturn: invested > 0 ? (marketValue - invested) / invested : 0,
    dayPnL,
    dayChange: yesterdayValue > 0 ? (marketValue - yesterdayValue) / yesterdayValue : 0,
    positions: priced.sort((a, b) => b.marketValue - a.marketValue),
  };
}

/**
 * Where a price sits within its 52-week range, as 0…1 (0 = at the low, 1 = at the high).
 * Drives the Watchlist's range gauge. Clamped, because a stale range can legitimately put
 * today's price outside it, and a gauge that renders off its own track is worse than one
 * pinned to the end.
 */
export function rangePosition(price: number, low: number, high: number): number {
  if (high <= low) return 0;
  return Math.min(1, Math.max(0, (price - low) / (high - low)));
}
